import type { PaginationParams } from '@/shared/types/domain/pagination.types';

export const shopKeys = {
  // Core shop keys
  all: ['shop'] as const,
  shops: (params?: PaginationParams) =>
    [...shopKeys.all, 'list', params] as const,
  shop: (id: string) => [...shopKeys.all, 'detail', id] as const,
  location: (id: string) => [...shopKeys.shop(id), 'location'] as const,
  socialMedia: (id: string) =>
    [...shopKeys.shop(id), 'socialMedia'] as const,
  hours: (id: string) => [...shopKeys.shop(id), 'hours'] as const,
  images: (id: string, type?: string) => [...shopKeys.shop(id), 'images', type] as const,
  imageReviewStatus: (id: string, type?: string) =>
    [...shopKeys.shop(id), 'image-review-status', type] as const,
  serviceCategories: () => [...shopKeys.all, 'serviceCategories'] as const,
  providerServiceCategories: (
    providerId: string,
    context?: { shopId?: string | null; profession?: string | null },
  ) =>
    [
      ...shopKeys.all,
      'provider-service-categories',
      providerId,
      context?.shopId ?? null,
      context?.profession ?? null,
    ] as const,

  // Dashboard keys
  dashboard: (shopId: string) =>
    [...shopKeys.all, 'dashboard', shopId] as const,
  dashboardMetrics: (shopId: string) =>
    [...shopKeys.dashboard(shopId), 'metrics'] as const,
  recentAppointments: (shopId: string, limit?: number) =>
    [...shopKeys.dashboard(shopId), 'appointments', { limit }] as const,
  topProviders: (shopId: string, limit?: number) =>
    [...shopKeys.dashboard(shopId), 'providers', { limit }] as const,

  // Staff keys
  staff: ['staff'] as const,
  providers: (filters?: Record<string, unknown>) =>
    [...shopKeys.staff, 'providers', filters] as const,
  provider: (id: string) =>
    [...shopKeys.staff, 'provider', id] as const,
  providerShops: (providerId: string | undefined) =>
    [...shopKeys.staff, 'provider', providerId, 'shops'] as const,
  staffMembers: (shopId: string, filters?: Record<string, unknown>) =>
    [...shopKeys.staff, 'staff', shopId, filters] as const,
  membership: (id: string) =>
    [...shopKeys.staff, 'membership', id] as const,
  providerMemberships: (
    providerId: string,
    filters?: Record<string, unknown>,
  ) =>
    [
      ...shopKeys.staff,
      'provider-memberships',
      providerId,
      filters,
    ] as const,
  invitations: (shopId: string) =>
    [...shopKeys.staff, 'invitations', shopId] as const,
  invitation: (code: string) =>
    [...shopKeys.staff, 'invitation', code] as const,
  rentalTiers: (membershipId: string) =>
    [...shopKeys.staff, 'rental-tiers', membershipId] as const,

  // Services keys
  services: ['services'] as const,
  providerServices: (providerId: string) =>
    [...shopKeys.services, 'provider', providerId] as const,
  // Shop-scoped provider services — use in shop-workspace contexts so cache
  // entries for the same provider don't leak across shops.
  shopProviderServices: (shopId: string, providerId: string) =>
    [...shopKeys.services, 'shop', shopId, 'provider', providerId] as const,
  shopServices: (shopId: string) =>
    [...shopKeys.services, 'shop', shopId] as const,
  service: (id: string) => [...shopKeys.services, 'detail', id] as const,

  // Add-on rule keys
  addOnRules: ['add-on-rules'] as const,
  providerAddOnRules: (providerId: string) =>
    [...shopKeys.addOnRules, 'provider', providerId] as const,
  baseServiceAddOnRules: (providerId: string, baseServiceId: string) =>
    [...shopKeys.addOnRules, 'base-service', providerId, baseServiceId] as const,

  // Scheduling keys
  scheduling: ['scheduling'] as const,
  schedules: (providerId: string) =>
    [...shopKeys.scheduling, 'provider', providerId] as const,
  schedule: (id: string) => [...shopKeys.scheduling, 'detail', id] as const,
  availability: (providerId: string, date: string, serviceIds: string[]) =>
    [
      ...shopKeys.scheduling,
      'availability',
      providerId,
      date,
      serviceIds,
    ] as const,

  // Metrics keys
  metrics: ['metrics'] as const,
  appointmentTracking: (appointmentId: string) =>
    [...shopKeys.metrics, 'tracking', appointmentId] as const,
  providerMetrics: (providerId: string, dateRange?: Record<string, unknown>) =>
    [...shopKeys.metrics, 'provider', providerId, dateRange] as const,
  overviewMetrics: (
    shopId: string,
    providerId?: string,
    dateRange?: Record<string, unknown>,
  ) =>
    [
      ...shopKeys.metrics,
      'overview',
      shopId,
      { providerId, dateRange },
    ] as const,
  timeSavedMetrics: (
    providerId?: string,
    dateRange?: Record<string, unknown>,
  ) =>
    [...shopKeys.metrics, 'time-saved', { providerId, dateRange }] as const,
  ownerAggregateMetrics: (dateRange?: Record<string, unknown>) =>
    [...shopKeys.metrics, 'owner-aggregate', dateRange] as const,

  // Invoice keys
  invoices: (shopId: string | null, filters?: Record<string, unknown>) =>
    [...shopKeys.all, 'invoices', shopId, filters] as const,
  suggestedFee: (providerId: string, periodStart: string, periodEnd: string) =>
    [...shopKeys.all, 'invoices', 'suggested-fee', providerId, periodStart, periodEnd] as const,
};
